import { useCallback, useEffect, useState } from "react";

import classNames from "classnames";

import { salesUrl, webAppURL } from "../../../config";
import useUrlHelper from "../../../hooks/urlHelper";
import { usePriceConfig } from "../../../hooks/usePriceConfig";
import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import { ElementClickAction } from "../../../lib/TrackingAdapter";
import { SubscriptionPeriod, SubscriptionTier } from "../../../types";
import PriceCard from "../PriceCard/PriceCard";
import SubscriptionPeriodPicker from "../SubscriptionPeriodPicker/SubscriptionPeriodPicker";

import style from "./PurchaseModalCards.module.css";

export interface PurchaseModalCardProps {
  selectedPeriod: SubscriptionPeriod;
  handleSubcriptionPeriodChange: (period: SubscriptionPeriod) => void;
}

export const PurchaseModalCards = (props: PurchaseModalCardProps) => {
  const tracker = useTracker();

  const [couponCode, setCouponCode] = useState("");
  const [query, setQuery] = useState("");
  const urlHelper = useUrlHelper();
  const { t, m } = useTranslation();

  const {
    data: priceConfigData,
    isLoading: isPriceConfigLoading,
    isError: isPriceConfigError,
  } = usePriceConfig();

  const postFrameHeightMessage = useCallback(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ FrameHeight: height }, "*");
    return;
  }, []);

  const messageEventListener = useCallback(
    (event: MessageEvent) => {
      if (event.data.InitialParameters) {
        if (event.data.InitialParameters.CouponCode) {
          setCouponCode(event.data.InitialParameters.CouponCode);
        }

        if (event.data.InitialParameters.query) {
          setQuery(event.data.InitialParameters.query);
        }
      }

      if (event.data.advertising && event.data.nonAdvertising) {
        tracker.trackClientIDs(event);
      }
    },
    [tracker]
  );

  useEffect(() => {
    window.addEventListener("message", messageEventListener);
    window.parent.postMessage("GetInitialParameters", "*");

    postFrameHeightMessage();
    window.addEventListener("resize", postFrameHeightMessage);
    return () => {
      window.removeEventListener("message", messageEventListener);
      window.removeEventListener("resize", postFrameHeightMessage);
    };
  }, [messageEventListener, postFrameHeightMessage]);

  const handleBuyNowClick = () => {
    return tracker.trackPricingClickFromPricingPage();
  };

  const getFreeButtonConfig = () => {
    return {
      href: () => `${webAppURL}/subscription`,
      text: t(m.Button.SelectBasic),
      trackingAction: ElementClickAction.START_SIGNUP_FLOW,
    };
  };

  const getPaidButtonConfig = (tier: SubscriptionTier) => {
    return {
      onClick: handleBuyNowClick,
      href: (period: SubscriptionPeriod) =>
        urlHelper.getBuyNowButtonURL(query, tier, period, couponCode),

      text: t(m.Purchase.StartFreeTrial),
      trackingAction: ElementClickAction.START_TRIAL_FLOW,
    };
  };

  const BasicFeatures = [
    t(m.Purchase.Plans.Basic.Feature01),
    t(m.Purchase.Plans.Basic.Feature02),
    t(m.Purchase.Plans.Basic.Feature04),
    t(m.Purchase.Plans.Basic.Feature05),
    t(m.Purchase.Plans.Basic.Feature06),
  ];

  const ProFeatures = [
    t(m.Purchase.Plans.Pro.Feature01),
    t(m.Purchase.Plans.Pro.Feature02),
    t(m.Purchase.Plans.Pro.Feature03),
    t(m.Purchase.Plans.Pro.Feature04),
    t(m.Purchase.Plans.Pro.Feature05),
    t(m.Purchase.Plans.Pro.Feature06),
    t(m.Purchase.Plans.Pro.Feature07),
  ];

  const TeamFeatures = [
    t(m.Purchase.Plans.Team.Feature01),
    t(m.Purchase.Plans.Team.Feature02),
    t(m.Purchase.Plans.Team.Feature03),
    t(m.Purchase.Plans.Team.Feature04),
    t(m.Purchase.Plans.Team.Feature05),
    t(m.Purchase.Plans.Team.Feature06),
    t(m.Purchase.Plans.Team.Feature07),
    t(m.Purchase.Plans.Team.Feature08),
  ];

  return (
    <div className={classNames(style.purchaseModalCards, style.embedded)}>
      <SubscriptionPeriodPicker
        period={props.selectedPeriod}
        onPeriodChange={props.handleSubcriptionPeriodChange}
        badgetext={t(m.Purchase.PriceMonthlySave, {
          savingsPercent: priceConfigData?.savingsPercent.toFixed(0),
        })}
      />

      <div className={`${style.content}`}>
        <PriceCard
          className="free"
          planType={t(m.Purchase.Plans.Basic.Name)}
          description={t(m.Purchase.Plans.Basic.Description)}
          price={{
            monthly: 0,
            yearly: 0,
            currencySymbol: priceConfigData?.currencySymbol ?? "$",
            availablePeriods: [],
          }}
          featuresTitle={`${t(m.Purchase.Plans.Basic.FeatureDescription)}`}
          features={BasicFeatures}
          button={getFreeButtonConfig()}
          selectedPeriod={SubscriptionPeriod.Monthly}
        />
        <PriceCard
          className="pro"
          planType={t(m.Purchase.Plans.Pro.Title)}
          description={t(m.Purchase.Plans.Pro.Description)}
          featured={true}
          price={priceConfigData}
          featuresTitle={`${t(m.Purchase.Plans.Pro.FeatureDescription)}`}
          features={ProFeatures}
          button={getPaidButtonConfig(SubscriptionTier.Lite)}
          isLoadingPrice={isPriceConfigLoading || isPriceConfigError}
          periodPrefix={"/seat"}
          selectedPeriod={props.selectedPeriod}
          shouldPostPurchaseURL={true}
        />
        <PriceCard
          className="team"
          planType={t(m.Purchase.Plans.Team.Title)}
          description={t(m.Purchase.Plans.Team.Description)}
          highlight={t(m.Purchase.Plans.Team.Highlight)}
          featuresTitle={t(m.Purchase.Plans.Team.FeatureDescription)}
          features={TeamFeatures}
          button={{
            href: () => salesUrl,
            text: t(m.Purchase.Plans.Team.ContactUs),
            trackingAction: ElementClickAction.GO_TO_CONTACT_SALES,
          }}
        />
      </div>
    </div>
  );
};

export default PurchaseModalCards;
